import React from 'react';
import classnames from 'classnames';
import styled from 'styled-components';

import { SENSITIVE_DATA_CLASS } from '@rover/constants/src/platform/sensitiveData.constants';
import { FONT_FAMILY } from '@rover/kibble/styles/Font';
import type { Props as LabelAndErrorFormGroupProps } from '@rover/react-lib/src/components/utils/LabelAndErrorFormGroup';
import LabelAndErrorFormGroup from '@rover/react-lib/src/components/utils/LabelAndErrorFormGroup';

const PLACEHOLDER_VALUE = 'placeholder';

const renderPlaceholder = (placeholder) => {
  if (placeholder) {
    return (
      <option disabled value={PLACEHOLDER_VALUE}>
        {placeholder}
      </option>
    );
  }
  return null;
};

export const StyledSelect = styled.select`
  background-color: ${({ theme }) => theme.background.primary};
  background-image: none;
  border-radius: ${({ theme }) => theme.radii.secondary};
  border: ${({ theme }) => `${theme.borderWidth.primary} solid ${theme.colors.border.primary}`};
  color: ${({ theme }) => theme.colors.text.secondary};
  display: block;
  font-family: ${FONT_FAMILY};
  font-size: ${({ theme }) => theme.text['100'].fontSize};
  height: 40px;
  line-height: 1.6;
  padding: ${({ theme }) =>
    `${theme.space['2x']} ${theme.space['4x']} ${theme.space['2x']} ${theme.space['1x']}`};
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
  width: 100%;
  box-shadow: none;

  &:disabled {
    border: ${({ theme }) =>
      `${theme.borderWidth.primary} solid ${theme.colors.interactive_border.disabled}`};
    background-color: ${({ theme }) => theme.background.interactive.disabled};
    color: ${({ theme }) => theme.colors.text.tertiary};
    cursor: not-allowed;
    opacity: 1;
  }
`;

export type Option<T extends string | number> = {
  value: T;
  title: string;
  disabled?: boolean;
};

export type Props<T extends string | number> = {
  value?: T;
  options: Array<Option<T>>;
  onChange: (arg0: T) => void;
  placeholder?: string;
  className?: string;
  sensitive?: boolean;
  autoComplete?: string;
  disabled?: boolean;
};

const Select = <T extends string | number>({
  value = '' as T,
  options,
  placeholder = '',
  onChange,
  className = '',
  sensitive = false,
  disabled,
  ...other
}: Props<T>): JSX.Element => {
  return (
    <StyledSelect
      className={classnames(className, {
        [SENSITIVE_DATA_CLASS]: sensitive,
      })}
      value={value || value === 0 ? value : PLACEHOLDER_VALUE}
      onChange={(e) => onChange(e.target.value as T)}
      disabled={disabled}
      {...other}
    >
      {renderPlaceholder(placeholder)}
      {options.map((option) => (
        <option
          value={option.value}
          key={option.value}
          disabled={typeof option.disabled === 'boolean' ? option.disabled : false}
        >
          {option.title as string}
        </option>
      ))}
    </StyledSelect>
  );
};

export { Select as UnwrappedSelect };

export default <T extends string | number>(
  props: LabelAndErrorFormGroupProps & { validationType: 'popover' | 'inline' } & Omit<
      Props<T>,
      'placeholder'
    >
): JSX.Element => <LabelAndErrorFormGroup {...props} InputComponent={Select} />;
